import React, {useState} from 'react';
import {User} from "types/User";
import ConfirmModal from "components/ConfirmModal";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Spinner from "../../../components/Spinner";
import Button from "../../../components/Button";
import {copyText} from "../../../services/clipboard";
import {Code} from "../../../components/Code";

type ViewRecoveryCodesProps = {
    user: User | null
    show: boolean
    onCancel: () => any
}

function ViewRecoveryCodes(props: ViewRecoveryCodesProps) {
    const {show, onCancel} = props

    const [isCopyingCode, setIsCopyingCode] = useState(false);

    const {
        data: recoveryCodes,
        isLoading: isRecoveryCodesLoading,
        refetch: refetchRecoveryCodes,
    } = useQuery('auth.twoFactorRevoveryCodes', ngage.auth.twoFactorRecoveryCodes, { enabled: show, cacheTime: 0 });

    const {mutate: regenerateCodes, isLoading: isGeneratingCodes, isError} = useMutation(ngage.auth.twoFactorRegenerateRecoveryCodes, {
        onSuccess: async () => await refetchRecoveryCodes(),
    });

    const isLoading = isRecoveryCodesLoading || isGeneratingCodes;

    async function handleCopyCodes() {
        await copyText({
            value: recoveryCodes?.join('\n'),
            onSuccess: () => {
                setIsCopyingCode(true);
                setTimeout(() => setIsCopyingCode(false), 2000);
            },
        });
    }

    return (
        <ConfirmModal
            show={show}
            type={ConfirmModal.types.INFO}
            title="Recovery Codes"
            body="You'll need these if your phone is lost/broken or you can't use your authenticator app. Keep them safe offline (e.g., printed copy) or in a trusted password manager."
            approveLabel="Disable"
            onCancel={onCancel}
        >
            <div className="flex flex-col gap-4 text-gray-900 text-sm mt-6">
                <div className='flex flex-col gap-6 items-center justify-center border border-gray-300 p-5'>
                    <div className='flex flex-wrap justify-center items-center min-h-[14rem]'>
                        {isLoading && <Spinner size={Spinner.sizes.SMALL} color={Spinner.colors.NEUTRAL} />}
                        {(!isLoading && !isError && recoveryCodes) && (
                            <div className='flex flex-col gap-2'>
                                {recoveryCodes.map(code => <Code code={code} key={code} />)}
                            </div>
                        )}
                        {(isError && !isLoading) && <p className='text-center max-w-[30ch] text-gray-500'>Sorry we couldn't regenerate your recovery codes, please try again.</p>}
                    </div>

                    <div className='flex gap-3'>
                        <Button
                          type='button'
                          color={Button.colors.WHITE}
                          size={Button.sizes.SMALL}
                          onClick={handleCopyCodes}
                        >
                            <span className={`${isCopyingCode ? 'text-green-600' : ''}`}>
                                {isCopyingCode ? 'Copied!' : 'Copy codes'}
                            </span>
                        </Button>

                        <Button
                          type='button'
                          color={Button.colors.WHITE}
                          size={Button.sizes.SMALL}
                          onClick={() => regenerateCodes()}
                          disabled={isGeneratingCodes || isRecoveryCodesLoading}
                        >
                            Regenerate codes
                        </Button>
                    </div>
                </div>
            </div>
        </ConfirmModal>
    );
}

export default ViewRecoveryCodes;